import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { BarChart3, Monitor, Smartphone, MapPin, Filter, ChevronDown, SortAsc, SortDesc, X, Calendar } from 'lucide-react';
import Pagination from '../components/Pagination';

function Bar({ label, value, max, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 4 }}>
        <span>{label}</span><span style={{ fontWeight: 600, color }}>{value}</span>
      </div>
      <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4 }}>
        <div style={{ height: '100%', width: `${max ? (value / max) * 100 : 0}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function ScanMap({ logs }) {
  const hasCoords = (logs || []).filter(l => l.latitude != null && l.longitude != null);
  if (hasCoords.length === 0) {
    return (
      <div style={{ height: 220, background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-200)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)', gap: 8 }}>
        <MapPin size={28} style={{ opacity: 0.4 }} />
        <div style={{ fontSize: '0.82rem' }}>No location data yet</div>
        <div style={{ fontSize: '0.74rem', textAlign: 'center', maxWidth: 260, lineHeight: 1.5 }}>Location is collected via browser geolocation when users allow access.</div>
      </div>
    );
  }
  const markers = JSON.stringify(hasCoords.map(l => ({ lat: l.latitude, lng: l.longitude, info: `${l.browser} · ${l.device} · ${new Date(l.scannedAt).toLocaleDateString()}` })));
  const html = `<!DOCTYPE html><html><head>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<style>html,body,#map{margin:0;padding:0;width:100%;height:100%;}</style>
</head><body><div id="map"></div><script>
const markers=${markers};
const map=L.map('map');
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'© OpenStreetMap'}).addTo(map);
const group=L.featureGroup();
markers.forEach(m=>{L.circleMarker([m.lat,m.lng],{radius:8,fillColor:'#7C3AED',color:'#4C1D95',weight:2,opacity:1,fillOpacity:0.85}).bindPopup('<b>Scan</b><br/>'+m.lat.toFixed(4)+', '+m.lng.toFixed(4)+'<br/><small>'+m.info+'</small>').addTo(group);});
group.addTo(map);
markers.length===1?map.setView([markers[0].lat,markers[0].lng],10):map.fitBounds(group.getBounds().pad(0.2));
<\/script></body></html>`;
  return <iframe srcDoc={html} style={{ width: '100%', height: 220, border: 'none', borderRadius: 10 }} title="Scan locations" sandbox="allow-scripts allow-same-origin" />;
}

function MapTooltip({ lat, lng }) {
  const [pos, setPos] = useState(null);
  const triggerRef = useRef();
  const hideTimer = useRef(null);
  if (lat == null || lng == null) return <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>—</span>;
  const showTooltip = () => {
    clearTimeout(hideTimer.current);
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ left: rect.left + rect.width / 2, top: rect.top - 12 });
  };
  const startHide = () => { hideTimer.current = setTimeout(() => setPos(null), 150); };
  const cancelHide = () => clearTimeout(hideTimer.current);
  const html = `<!DOCTYPE html><html><head><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><style>html,body,#map{margin:0;padding:0;width:100%;height:100%;}</style></head><body><div id="map"></div><script>const map=L.map('map',{zoomControl:true,attributionControl:false,scrollWheelZoom:true});L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);map.setView([${lat},${lng}],13);L.circleMarker([${lat},${lng}],{radius:9,fillColor:'#7C3AED',color:'#4C1D95',weight:2,opacity:1,fillOpacity:0.9}).addTo(map);<\/script></body></html>`;
  const popup = pos ? ReactDOM.createPortal(
    <div onMouseEnter={cancelHide} onMouseLeave={startHide} style={{ position: 'fixed', left: pos.left, top: pos.top, transform: 'translate(-50%, -100%)', zIndex: 99999, width: 500, height: 250, borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', border: '2px solid #9333EA', background: '#fff', pointerEvents: 'all', cursor: 'default' }}>
      <iframe srcDoc={html} style={{ width: '100%', height: '100%', border: 'none' }} title="loc" sandbox="allow-scripts allow-same-origin" />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', fontSize: '0.62rem', color: '#4C1D95', background: 'rgba(255,255,255,0.92)', padding: '3px 0', fontWeight: 600, pointerEvents: 'none' }}>{lat.toFixed(5)}, {lng.toFixed(5)}</div>
      <div style={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid #9333EA', pointerEvents: 'none' }} />
    </div>, document.body
  ) : null;
  return (
    <><span ref={triggerRef} onMouseEnter={showTooltip} onMouseLeave={startHide} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--purple-800)', fontSize: '0.75rem', fontWeight: 500, background: 'var(--purple-100)', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}><MapPin size={11} />{lat.toFixed(3)}, {lng.toFixed(3)}</span>{popup}</>
  );
}

// Searchable doctor dropdown
function DoctorSearch({ doctors, selectedDoctor, onSelect, style }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();

  const filtered = query.trim()
    ? doctors.filter(d => d.name.toLowerCase().includes(query.toLowerCase()))
    : doctors;

  const selected = doctors.find(d => d._id === selectedDoctor);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      {/* Trigger */}
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', border: '1.5px solid var(--gray-200)', borderRadius: 10, cursor: 'pointer', background: 'white', fontSize: '0.875rem', color: selected ? 'var(--gray-800)' : 'var(--gray-400)' }}>
        <span>{selected ? selected.name : 'Select a doctor to view stats...'}</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
          <path d="M2 4l5 5 5-5" stroke="var(--gray-400)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'white', border: '1.5px solid var(--gray-200)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 9999, overflow: 'hidden' }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--gray-100)' }}>
            <div style={{ position: 'relative' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="6" cy="6" r="4" stroke="var(--gray-400)" strokeWidth="1.5"/>
                <path d="M9.5 9.5L12 12" stroke="var(--gray-400)" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search doctors..."
                style={{ width: '100%', padding: '6px 10px 6px 30px', border: '1.5px solid var(--gray-200)', borderRadius: 7, fontSize: '0.82rem', outline: 'none', fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box' }}
                onClick={e => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: '0.82rem', color: 'var(--gray-400)', textAlign: 'center' }}>No doctors found</div>
            ) : filtered.map(d => (
              <div key={d._id} onClick={() => { onSelect(d._id); setOpen(false); setQuery(''); }}
                style={{ padding: '9px 14px', fontSize: '0.875rem', cursor: 'pointer', background: d._id === selectedDoctor ? 'var(--purple-100)' : 'white', color: d._id === selectedDoctor ? 'var(--purple-800)' : 'var(--gray-800)', fontWeight: d._id === selectedDoctor ? 600 : 400 }}
                onMouseEnter={e => { if (d._id !== selectedDoctor) e.currentTarget.style.background = 'var(--gray-50)'; }}
                onMouseLeave={e => { if (d._id !== selectedDoctor) e.currentTarget.style.background = 'white'; }}>
                {d.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Stats() {
  const [overview, setOverview] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);

  // All filter state — onChange handlers only set state, never call fetchLogs directly
  const [logSort, setLogSort] = useState('scannedAt');
  const [logOrder, setLogOrder] = useState('desc');
  const [filterBrowser, setFilterBrowser] = useState('');
  const [filterDevice, setFilterDevice] = useState('');
  const [filterOs, setFilterOs] = useState('');
  const [filterHasLoc, setFilterHasLoc] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logLimit, setLogLimit] = useState(50);

  useEffect(() => {
    Promise.all([
      axios.get('/api/stats/overview'),
      axios.get('/api/doctors?pagination=false&fields=_id,name'),
    ])
      .then(([s, d]) => { setOverview(s.data); setDoctors(d.data.data); })
      .finally(() => setLoading(false));
  }, []);

  // ── fetchLogs: no params, reads everything from state via closure ──────────
  const fetchLogs = useCallback(async () => {
    if (!selectedDoctor) return;
    setLogsLoading(true);
    try {
      const p = {
        page: logPage, limit: logLimit,
        sort: logSort, order: logOrder,
        browser: filterBrowser, device: filterDevice,
        os: filterOs, hasLocation: filterHasLoc,
        dateFrom, dateTo,
      };
      const qs = new URLSearchParams();
      Object.entries(p).forEach(([k, v]) => { if (v !== '' && v != null) qs.set(k, String(v)); });
      const res = await axios.get(`/api/stats/doctor/${selectedDoctor}?${qs.toString()}`);
      setSummary({
        totalScans: res.data.totalScans,
        browsers: res.data.browsers,
        devices: res.data.devices,
        os: res.data.os,
        logsWithCoords: res.data.logsWithCoords,
      });
      setLogs(res.data.recentLogs);
      setPagination(res.data.pagination);
    } catch { }
    finally { setLogsLoading(false); }
  }, [
    selectedDoctor, logPage, logLimit,
    logSort, logOrder,
    filterBrowser, filterDevice, filterOs, filterHasLoc,
    dateFrom, dateTo,
  ]);

  // ── Single useEffect — one request per dependency change, no duplicates ────
  useEffect(() => {
    if (selectedDoctor) fetchLogs();
  }, [fetchLogs]);

  const handleDoctorChange = (id) => {
    setSelectedDoctor(id);
    setSummary(null);
    setLogs([]);
    setLogPage(1);
    setFilterBrowser('');
    setFilterDevice('');
    setFilterOs('');
    setFilterHasLoc('');
    setDateFrom('');
    setDateTo('');
  };

  // Only update state — useEffect will trigger the fetch automatically
  const handleLogSort = (field) => {
    const newOrder = logSort === field && logOrder === 'desc' ? 'asc' : 'desc';
    setLogSort(field);
    setLogOrder(newOrder);
    setLogPage(1);
  };

  const clearLogFilters = () => {
    setFilterBrowser('');
    setFilterDevice('');
    setFilterOs('');
    setFilterHasLoc('');
    setDateFrom('');
    setDateTo('');
    setLogPage(1);
  };

  const activeLogFilters = [filterBrowser, filterDevice, filterOs, filterHasLoc, dateFrom, dateTo].filter(Boolean).length;

  const SortTh = ({ field, label }) => (
    <th onClick={() => handleLogSort(field)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {logSort === field
          ? (logOrder === 'asc' ? <SortAsc size={12} color="var(--purple-800)" /> : <SortDesc size={12} color="var(--purple-800)" />)
          : <SortAsc size={12} style={{ opacity: 0.25 }} />}
      </span>
    </th>
  );

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <div className="spinner" style={{ width: 36, height: 36, borderColor: 'rgba(107,33,168,0.2)', borderTopColor: 'var(--purple-800)' }} />
    </div>
  );

  const maxScan = Math.max(...(overview?.last7?.map(d => d.scans) || [1]), 1);
  const browsers = summary?.browsers || {};
  const devices = summary?.devices || {};
  const osMap = summary?.os || {};

  return (
    <div>
      <div className="page-header">
        <div><h1>Analytics</h1><p>Track QR scan performance across all campaigns</p></div>
      </div>

      {/* 7-day chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <BarChart3 size={18} color="var(--purple-800)" />
          <h3 style={{ fontSize: '1rem' }}>Daily Scans — Last 7 Days</h3>
          <span className="badge badge-purple" style={{ marginLeft: 'auto' }}>{overview?.totalScans || 0} total</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 180 }}>
          {(overview?.last7 || []).map((day, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: '0.72rem', color: day.scans > 0 ? 'var(--purple-800)' : 'var(--gray-300)', fontWeight: 700 }}>{day.scans}</div>
              <div style={{ width: '100%', borderRadius: '6px 6px 0 0', background: day.scans > 0 ? 'linear-gradient(180deg,#a855f7,#6B21A8)' : 'var(--gray-100)', height: `${Math.max((day.scans / maxScan) * 140, 4)}px`, transition: 'height 0.6s ease' }} />
              <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', textAlign: 'center' }}>{day.date}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Doctor selector */}
      <div className="card">
        <h3 style={{ fontSize: '1rem', marginBottom: 16 }}>Doctor-level Analytics</h3>
        {/* <div style={{ marginBottom: summary ? 20 : 0 }}>
          <select className="form-input" style={{ maxWidth: 340 }} value={selectedDoctor} onChange={e => handleDoctorChange(e.target.value)}>
            <option value="">Select a doctor to view stats...</option>
            {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
          </select>
        </div> */}
         <DoctorSearch
          doctors={doctors}
          selectedDoctor={selectedDoctor}
          onSelect={handleDoctorChange}
          style={{ maxWidth: 340, marginBottom: summary ? 20 : 0 }}
        />

        {selectedDoctor && !summary && logsLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" style={{ width: 28, height: 28, borderColor: 'rgba(107,33,168,0.2)', borderTopColor: 'var(--purple-800)' }} />
          </div>
        )}

        {summary && (
          <div>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total Scans', value: summary.totalScans || 0, color: '#6B21A8' },
                { label: 'With Location', value: (summary.logsWithCoords || []).length, color: '#059669' },
                { label: 'Browsers Used', value: Object.keys(browsers).length, color: '#0891B2' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--gray-100)' }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.value}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 500 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontWeight: 600, fontSize: '0.82rem' }}>
                <MapPin size={14} color="var(--purple-800)" /> Scan Locations Map
                <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400, marginLeft: 4 }}>
                  {(summary.logsWithCoords || []).length} with location · hover rows for popup map
                </span>
              </div>
              <ScanMap logs={summary.logsWithCoords || []} />
            </div>

            {/* Browser & Device */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontWeight: 600, fontSize: '0.82rem' }}><Monitor size={14} /> By Browser</div>
                {Object.keys(browsers).length === 0
                  ? <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>No data yet</div>
                  : Object.entries(browsers).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) =>
                    <Bar key={k} label={k} value={v} max={Math.max(...Object.values(browsers))} color="#0891B2" />)}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontWeight: 600, fontSize: '0.82rem' }}><Smartphone size={14} /> By Device</div>
                {Object.keys(devices).length === 0
                  ? <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>No data yet</div>
                  : Object.entries(devices).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) =>
                    <Bar key={k} label={k} value={v} max={Math.max(...Object.values(devices))} color="#059669" />)}
              </div>
            </div>

            {/* ── Log filters toolbar ── */}
            <div style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '12px 14px', marginBottom: 14, border: '1px solid var(--gray-100)' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontWeight: 600, whiteSpace: 'nowrap' }}>Filter Logs:</span>

                {/* Browser filter */}
                <div style={{ position: 'relative' }}>
                  <Filter size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: filterBrowser ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none' }} />
                  <select
                    value={filterBrowser}
                    onChange={e => { setFilterBrowser(e.target.value); setLogPage(1); }}
                    style={{ paddingLeft: 24, paddingRight: 22, height: 34, borderRadius: 8, border: `1.5px solid ${filterBrowser ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', outline: 'none', color: filterBrowser ? 'var(--purple-800)' : 'var(--gray-600)', background: filterBrowser ? 'var(--purple-100)' : 'white', appearance: 'none', cursor: 'pointer' }}>
                    <option value="">All Browsers</option>
                    {Object.keys(browsers).map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <ChevronDown size={11} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)' }} />
                </div>

                {/* Device filter */}
                <div style={{ position: 'relative' }}>
                  <Filter size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: filterDevice ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none' }} />
                  <select
                    value={filterDevice}
                    onChange={e => { setFilterDevice(e.target.value); setLogPage(1); }}
                    style={{ paddingLeft: 24, paddingRight: 22, height: 34, borderRadius: 8, border: `1.5px solid ${filterDevice ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', outline: 'none', color: filterDevice ? 'var(--purple-800)' : 'var(--gray-600)', background: filterDevice ? 'var(--purple-100)' : 'white', appearance: 'none', cursor: 'pointer' }}>
                    <option value="">All Devices</option>
                    {Object.keys(devices).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <ChevronDown size={11} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)' }} />
                </div>

                {/* OS filter */}
                <div style={{ position: 'relative' }}>
                  <Filter size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: filterOs ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none' }} />
                  <select
                    value={filterOs}
                    onChange={e => { setFilterOs(e.target.value); setLogPage(1); }}
                    style={{ paddingLeft: 24, paddingRight: 22, height: 34, borderRadius: 8, border: `1.5px solid ${filterOs ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', outline: 'none', color: filterOs ? 'var(--purple-800)' : 'var(--gray-600)', background: filterOs ? 'var(--purple-100)' : 'white', appearance: 'none', cursor: 'pointer' }}>
                    <option value="">All OS</option>
                    {Object.keys(osMap).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <ChevronDown size={11} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)' }} />
                </div>

                {/* Location filter */}
                <div style={{ position: 'relative' }}>
                  <MapPin size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: filterHasLoc ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none' }} />
                  <select
                    value={filterHasLoc}
                    onChange={e => { setFilterHasLoc(e.target.value); setLogPage(1); }}
                    style={{ paddingLeft: 24, paddingRight: 22, height: 34, borderRadius: 8, border: `1.5px solid ${filterHasLoc ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', outline: 'none', color: filterHasLoc ? 'var(--purple-800)' : 'var(--gray-600)', background: filterHasLoc ? 'var(--purple-100)' : 'white', appearance: 'none', cursor: 'pointer' }}>
                    <option value="">All Locations</option>
                    <option value="true">Has Location</option>
                    <option value="false">No Location</option>
                  </select>
                  <ChevronDown size={11} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)' }} />
                </div>

                {/* Date range */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Calendar size={13} style={{ color: 'var(--gray-400)' }} />
                  <input
                    type="date" value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setLogPage(1); }}
                    style={{ height: 34, borderRadius: 8, border: `1.5px solid ${dateFrom ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', padding: '0 8px', outline: 'none', background: dateFrom ? 'var(--purple-100)' : 'white', color: dateFrom ? 'var(--purple-800)' : 'var(--gray-600)' }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>to</span>
                  <input
                    type="date" value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setLogPage(1); }}
                    style={{ height: 34, borderRadius: 8, border: `1.5px solid ${dateTo ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', padding: '0 8px', outline: 'none', background: dateTo ? 'var(--purple-100)' : 'white', color: dateTo ? 'var(--purple-800)' : 'var(--gray-600)' }} />
                </div>

                {activeLogFilters > 0 && (
                  <button className="btn btn-sm btn-outline" onClick={clearLogFilters} style={{ height: 34 }}>
                    <X size={12} /> Clear ({activeLogFilters})
                  </button>
                )}
              </div>
            </div>

            {/* Pagination for logs */}
            <Pagination
              page={pagination.page} limit={pagination.limit} total={pagination.total}
              onPageChange={p => setLogPage(p)}
              onLimitChange={l => { setLogLimit(l); setLogPage(1); }}
            />

            {/* ── Logs table ── */}
            <div style={{ border: '1px solid var(--gray-100)', borderRadius: 10, overflow: 'hidden' }}>
              {logsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
                  <div className="spinner" style={{ width: 24, height: 24, borderColor: 'rgba(107,33,168,0.2)', borderTopColor: 'var(--purple-800)' }} />
                </div>
              ) : (
                <div className="table-container" style={{ maxHeight: 320, overflow: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <SortTh field="ipAddress" label="IP Address" />
                        <th>Map Location</th>
                        <SortTh field="browser" label="Browser" />
                        <SortTh field="os" label="OS" />
                        <SortTh field="device" label="Device" />
                        <SortTh field="scannedAt" label="Time" />
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                            No scans match the current filters
                          </td>
                        </tr>
                      ) : logs.map(l => (
                        <tr key={l._id}>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{l.ipAddress}</td>
                          <td><MapTooltip lat={l.latitude} lng={l.longitude} /></td>
                          <td style={{ fontSize: '0.8rem' }}>{l.browser}</td>
                          <td style={{ fontSize: '0.8rem' }}>{l.os}</td>
                          <td style={{ fontSize: '0.8rem' }}>{l.device}</td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{new Date(l.scannedAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}